import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ListPlus, Loader2, Lock, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader, EmptyState, ConfirmDialog, Field } from "../components/common";
import { PerusahaanSelect, Stepper } from "../components/inputs";
import { api, errMsg } from "../lib/api";
import { fmtDate, num, rp, todayISO } from "../lib/format";

const isInt = (v) => /^\d+$/.test(v || "");
const toN = (v) => (isInt(v) ? parseInt(v, 10) : 0);

const Col = ({ label, children }) => (
  <div className="flex flex-col items-center gap-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</span>{children}</div>
);

export default function VoucherMassal() {
  const [anak, setAnak] = useState(null);
  const [perusahaan, setPerusahaan] = useState([]);
  const [cfg, setCfg] = useState({});
  const tarif = cfg.tarif_per_voucher || 0;
  const [lunas, setLunas] = useState([]);
  const [tanggal, setTanggal] = useState(todayISO());
  const [perId, setPerId] = useState("");
  const [q, setQ] = useState("");
  const [vals, setVals] = useState({});
  const [existing, setExisting] = useState({});
  const [invalidId, setInvalidId] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/anak", { params: { aktif: true } }).then((r) => setAnak(r.data));
    api.get("/perusahaan").then((r) => setPerusahaan(r.data));
    api.get("/settings").then((r) => setCfg(r.data));
    api.get("/payroll/history").then((r) => setLunas(r.data));
  }, []);

  const loadExisting = () => {
    if (!tanggal) return;
    api.get("/voucher", { params: { tanggal } }).then((r) =>
      setExisting(r.data.reduce((m, v) => ({ ...m, [v.anak_id]: (m[v.anak_id] || 0) + v.tamu }), {})));
  };
  useEffect(loadExisting, [tanggal]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => (anak || []).filter((a) =>
    (!perId || a.perusahaan_id === perId) && (!q || a.nama.toLowerCase().includes(q.toLowerCase()))), [anak, perId, q]);

  const filled = useMemo(() => (anak || []).filter((a) => toN(vals[a.id]) > 0), [anak, vals]);
  const totalTamu = filled.reduce((s, a) => s + toN(vals[a.id]), 0);
  const locked = tanggal && lunas.find((h) => tanggal >= h.start && tanggal <= h.end);

  const setVal = (id, v) => { setVals((s) => ({ ...s, [id]: v })); if (invalidId === id) setInvalidId(""); };

  const askSave = () => {
    if (locked) return toast.error(`Tanggal ${fmtDate(tanggal)} terkunci karena periode ${locked.label} sudah lunas.`);
    const badV = (anak || []).find((a) => vals[a.id] !== undefined && vals[a.id] !== "" && !isInt(vals[a.id]));
    if (badV) { setInvalidId(badV.id); return toast.error(`${badV.nama}: jumlah tamu harus bilangan bulat ≥ 0`); }
    if (!filled.length) return toast.error("Isi jumlah tamu minimal 1 pada salah satu anak");
    setConfirm(true);
  };

  const save = async () => {
    setConfirm(false);
    setSaving(true);
    try {
      const { data } = await api.post("/voucher/bulk", { tanggal, items: filled.map((a) => ({ anak_id: a.id, tamu: toN(vals[a.id]) })) });
      toast.success(`${data.created} entri voucher disimpan (${num(data.total_voucher)} voucher)`);
      setVals({});
      loadExisting();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <Link to="/voucher" data-testid="massal-back" className="mb-3 inline-flex h-10 items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-900"><ArrowLeft className="h-4 w-4" />Voucher</Link>
      <PageHeader title="Input Massal" testid="massal-title" subtitle={`Isi jumlah tamu untuk banyak anak sekaligus · ${rp(tarif)} / voucher`} />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-[200px_1fr_1fr]">
        <Field label="Tanggal"><Input type="date" data-testid="massal-tanggal" className="h-10 bg-white" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></Field>
        <Field label="Perusahaan"><PerusahaanSelect perusahaan={perusahaan} value={perId} onChange={setPerId} allowAll testid="massal-filter-perusahaan" /></Field>
        <div className="col-span-2 md:col-span-1"><Field label="Cari">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input data-testid="massal-search" className="h-10 bg-white pl-10" placeholder="Cari nama anak…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </Field></div>
      </div>

      {locked && (
        <div data-testid="massal-locked-banner" className="mb-4 flex items-start gap-2 rounded-xl border border-gray-300 bg-gray-100 px-4 py-3 text-sm text-gray-700">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />Tanggal {fmtDate(tanggal)} terkunci karena periode {locked.label} sudah lunas. Pilih tanggal lain.
        </div>
      )}

      {anak && visible.length === 0 ? (
        <EmptyState icon={ListPlus} title="Tidak ada anak aktif" desc={q || perId ? "Tidak ada yang cocok dengan filter." : "Tambahkan anak terlebih dahulu di menu Anak."} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="massal-list">
          {visible.map((a) => (
            <div key={a.id} data-testid={`massal-row-${a.id}`} className={`flex flex-col gap-3 border-b border-gray-100 px-4 py-3 last:border-0 sm:flex-row sm:items-center ${invalidId === a.id ? "bg-red-50" : ""}`}>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 break-words font-semibold text-gray-900">{a.nama}</p>
                <p className="break-words text-[13px] text-gray-500">{a.perusahaan_nama || "Tanpa perusahaan"}</p>
                {existing[a.id] > 0 && (
                  <p data-testid={`massal-existing-${a.id}`} className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                    <Info className="h-3 w-3" />sudah ada {existing[a.id]} voucher hari ini
                  </p>
                )}
              </div>
              <Col label="Voucher"><Stepper testid={`massal-${a.id}`} className="w-36" value={vals[a.id] ?? "0"} onChange={(v) => setVal(a.id, v)} invalid={invalidId === a.id} /></Col>
            </div>
          ))}
        </div>
      )}

      <div className="sticky bottom-20 z-20 mt-5 md:bottom-4">
        <div className="flex flex-col gap-3 rounded-2xl border border-primary/20 bg-white/90 p-4 shadow-lg backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between" data-testid="massal-summary">
          <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-left">
            <div><p className="text-[11px] uppercase tracking-wider text-gray-500">Anak</p><p data-testid="massal-sum-anak" className="font-mono text-lg font-semibold">{filled.length}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-gray-500">Voucher</p><p data-testid="massal-sum-voucher" className="font-mono text-lg font-semibold">{num(totalTamu)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-gray-500">Total</p><p data-testid="massal-sum-rp" className="font-mono text-lg font-semibold text-primary">{rp(totalTamu * tarif)}</p></div>
          </div>
          <Button data-testid="massal-save-btn" disabled={saving || !filled.length || locked} onClick={askSave} className="h-10 rounded-full px-5 text-base active:scale-[0.98]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan Semua
          </Button>
        </div>
      </div>

      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Simpan semua voucher?"
        description={`${filled.length} anak · ${num(totalTamu)} voucher · ${rp(totalTamu * tarif)} pada tanggal ${fmtDate(tanggal)}. Anak dengan 0 tamu dilewati.`}
        confirmLabel="Ya, simpan" onConfirm={save} testid="massal-confirm" />
    </div>
  );
}
